import React from 'react';
import { Box } from '@mui/material';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import SalonTemplateAbout from '../blocks/SalonTemplateAbout';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateIdentity from '../blocks/SalonTemplateIdentity';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';


const AtelierMuseTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { layout, tokens: theme } = useSalonPublicTheme();
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const mobile = viewport === 'mobile';

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="atelier_muse"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: theme.salon.background,
      }}
    >
      <SalonTemplateIdentity
        data={{
          ...identity,
          heroPhoto: photos[0]?.src,
          averageRating: viewModel?.averageRating,
          reviewCount: reviews.length,
          isFavorite: viewModel?.capabilities?.isFavorite,
        }}
        variant="immersive"
        mode={mode}
        onBack={actions?.onBack}
        onToggleFavorite={actions?.onToggleFavorite}
        onOpenMobileInfo={actions?.onOpenMobileInfo}
        onOpenHeaderInfo={actions?.onOpenHeaderInfo}
        onBook={actions?.onOpenBooking}
        onContact={actions?.onContact}
        clientChatEnabled={identity.raw?.client_chat_enabled}
      />

      <Box
        data-testid="atelier-muse-scroll-region"
        sx={{
          flex: 1,
          minHeight: mobile ? 'auto' : 0,
          width: '100%',
          overflowY: mobile ? 'visible' : 'auto',
          overflowX: 'hidden',
          [HIGH_DENSITY_TABLET_MEDIA]: { overflowY: 'visible' },
        }}
      >
        <Box
          component="main"
          data-testid="atelier-muse-content"
          data-layout={mobile ? 'single-column' : 'editorial-stack'}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: 1480,
            mx: 'auto',
            px: { xs: 2, md: 5 },
            py: { xs: 3, md: 7 },
            boxSizing: 'border-box',
          }}
        >
          <SalonTemplateGallery
            data={photos}
            salonId={identity.id}
            variant="editorial"
            visible={visibility.gallery}
            mode={mode}
            onSelectPhoto={actions?.onSelectPhoto}
            onNavigatePhoto={actions?.onNavigatePhoto}
            selectedIndex={publicState.photoIndex}
          />
          <SalonTemplateTeam
            data={viewModel?.team}
            variant="creators"
            visible={visibility.team}
            mode={mode}
            onSelectMember={actions?.onSelectTeamMember}
          />
          <SalonTemplateServices
            data={viewModel?.serviceCategories}
            variant="editorial"
            mode={mode}
            loading={Boolean(publicState.servicesLoading)}
            onBookService={actions?.onBookService}
          />
          <SalonTemplateReviews
            data={{
              items: reviews,
              averageRating: viewModel?.averageRating,
              isAuthenticated: Boolean(publicState.isAuthenticated),
              isLoading: Boolean(publicState.reviewsLoading),
              error: publicState.reviewsError || null,
            }}
            variant="editorial"
            visible={visibility.reviews}
            mode={mode}
            onOpenReviewComposer={actions?.onOpenReviewComposer}
            onOpenAllReviews={actions?.onOpenAllReviews}
          />
          <SalonTemplateOpeningHours
            data={{ hours: viewModel?.openingHours, closures: viewModel?.closures }}
            variant="table"
            visible={visibility.opening_hours}
            markClosuresAsBlock={false}
          />
          <SalonTemplateAbout
            data={identity}
            variant="editorial"
            visible={Boolean(identity.description)}
          />
          <SalonTemplateContactLocation
            data={identity}
            variant="contact"
            visible={visibility.location}
            mode={mode}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AtelierMuseTemplate;
